/**
 * Status bar: the steps of a flow as chips in one row, joined by lines – used by the evidence
 * detail. The permit procedure shows its flow in the page header instead (`ProcedureStage`).
 *
 * Three states, told apart at a glance:
 * - done: outlined in the brand color, normal weight
 * - current: solid in its status color, one size larger, with a light shadow
 * - open: light grey, normal weight
 * Up to the current step the line is brand-colored, after it grey.
 *
 * A step can carry a side branch it is in right now (`zweig`): the step is shown as reached and
 * the branch as current, joined into one chip ("Eingang bestätigt | Nachforderung offen"). The
 * other way round, a current step can carry a second line of information (`hinweis`) in the done
 * look ("In Erstellung | 3 / 6 Nachweise abgeschlossen").
 *
 * The row never wraps. When the width does not hold every step at the minimum line length, steps
 * collapse into "…" (the hidden steps in the tooltip), keeping the current step, its neighbours,
 * the last and the first one as long as possible. Widths are measured from a hidden copy of the
 * chips, so the bar adapts to any label length and to resizing.
 *
 * Domain-free on purpose: callers resolve labels and colors (`getEvidenceStatusVisual`,
 * `getProcedureStatusVisual`) and pass finished steps.
 */
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import StatusChip from "./StatusChip";
import { colors } from "../../../styles/theme";
import { StatusVisual } from "../../../utils/colors/statusVisual";

export interface StatusStrahlSchritt {
  key: string;
  /** Label and colors of the chip while it is the current step */
  visual: StatusVisual;
  /** Side branch the flow is in at this step – then it is the current part of the chip */
  zweig?: StatusVisual;
  /** Second line of information while the step is current, e.g. "3 / 6 Nachweise abgeschlossen" */
  hinweis?: string;
}

interface StatusStrahlProps {
  schritte: StatusStrahlSchritt[];
  /** Index of the current step; below 0 all are open, at or beyond the length all are done */
  aktuell: number;
  ariaLabel: string;
}

/** Lines keep this length (px) before steps collapse into "…" – the length in the evidence detail */
const LINE_MIN = 48;
/** Horizontal margin on each side of a line (`mx: 1`) */
const LINE_MARGIN = 8;

type Eintrag =
  | { art: "schritt"; index: number }
  | { art: "auslassung"; indizes: number[] };

/** The row for a set of visible steps: hidden runs become one "…" each */
const eintraegeFuer = (anzahl: number, sichtbar: Set<number>): Eintrag[] => {
  const eintraege: Eintrag[] = [];
  for (let index = 0; index < anzahl; index++) {
    if (sichtbar.has(index)) {
      eintraege.push({ art: "schritt", index });
      continue;
    }
    const letzter = eintraege[eintraege.length - 1];
    if (letzter?.art === "auslassung") letzter.indizes.push(index);
    else eintraege.push({ art: "auslassung", indizes: [index] });
  }
  return eintraege;
};

/** Order in which steps stay visible: current, next, previous, last, first, then outward */
const prioritaet = (anzahl: number, aktuell: number): number[] => {
  const anker = Math.min(Math.max(aktuell, 0), anzahl - 1);
  const reihe = [anker, anker + 1, anker - 1, anzahl - 1, 0];
  for (let abstand = 2; abstand < anzahl; abstand++) {
    reihe.push(anker + abstand, anker - abstand);
  }
  return [...new Set(reihe.filter((i) => i >= 0 && i < anzahl))];
};

const StatusStrahl: React.FC<StatusStrahlProps> = ({
  schritte,
  aktuell,
  ariaLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const messRefs = useRef<(HTMLDivElement | null)[]>([]);
  const auslassungRef = useRef<HTMLDivElement>(null);
  const [breite, setBreite] = useState<number | null>(null);
  const [masse, setMasse] = useState<{
    schritte: number[];
    auslassung: number;
  } | null>(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const beobachter = new ResizeObserver(([eintrag]) =>
      setBreite(eintrag.contentRect.width),
    );
    beobachter.observe(element);
    return () => beobachter.disconnect();
  }, []);

  // Measure the chips once per set of steps; the hidden copy has the same states as the bar.
  useLayoutEffect(() => {
    setMasse({
      schritte: schritte.map(
        (_, index) => messRefs.current[index]?.offsetWidth ?? 0,
      ),
      auslassung: auslassungRef.current?.offsetWidth ?? 0,
    });
  }, [schritte, aktuell]);

  const sichtbar = useMemo(() => {
    const alle = new Set(schritte.map((_, index) => index));
    if (breite === null || !masse || schritte.length === 0) return alle;
    const passt = (kandidaten: Set<number>) => {
      const eintraege = eintraegeFuer(schritte.length, kandidaten);
      const chips = eintraege.reduce(
        (summe, e) =>
          summe +
          (e.art === "schritt" ? masse.schritte[e.index] : masse.auslassung),
        0,
      );
      return (
        chips + (eintraege.length - 1) * (LINE_MIN + 2 * LINE_MARGIN) <= breite
      );
    };
    if (passt(alle)) return alle;
    const auswahl = new Set<number>();
    prioritaet(schritte.length, aktuell).forEach((index, rang) => {
      const probe = new Set(auswahl).add(index);
      // The first step always stays, even if nothing else fits.
      if (rang === 0 || passt(probe)) auswahl.add(index);
    });
    return auswahl;
  }, [breite, masse, schritte, aktuell]);

  const zustand = (index: number) =>
    index < aktuell ? "erledigt" : index === aktuell ? "aktuell" : "offen";

  /** Done look of a label – a reached step, or the second line of information of the current one */
  const erledigtChip = (label: string, size: "small" | "medium") => (
    <StatusChip
      label={label}
      color={colors.brand.primary}
      background={colors.brand.primary}
      variante="outlined"
      size={size}
    />
  );

  /** Two chips joined into one: the outlined part loses its inner border */
  const zweiteilig = (
    links: React.ReactNode,
    rechts: React.ReactNode,
    umrandet: "links" | "rechts",
  ) => (
    <Box
      sx={{
        display: "inline-flex",
        "& .MuiChip-root:first-of-type": {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          ...(umrandet === "links" ? { borderRight: "none" } : {}),
        },
        "& .MuiChip-root:last-of-type": {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          ...(umrandet === "rechts" ? { borderLeft: "none" } : {}),
        },
      }}
    >
      {links}
      {rechts}
    </Box>
  );

  const chip = (
    schritt: StatusStrahlSchritt,
    index: number,
  ): React.ReactNode => {
    switch (zustand(index)) {
      case "erledigt":
        return erledigtChip(schritt.visual.label, "small");
      case "aktuell":
        if (schritt.zweig) {
          // The station as reached, the branch it is in as current.
          return zweiteilig(
            erledigtChip(schritt.visual.label, "medium"),
            <StatusChip {...schritt.zweig} variante="solid" size="medium" />,
            "links",
          );
        }
        if (schritt.hinweis) {
          // The station as current, its second line of information in the done look.
          return zweiteilig(
            <StatusChip {...schritt.visual} variante="solid" size="medium" />,
            erledigtChip(schritt.hinweis, "medium"),
            "rechts",
          );
        }
        return <StatusChip {...schritt.visual} variante="solid" size="medium" />;
      default:
        return (
          <StatusChip
            label={schritt.visual.label}
            color={colors.text.secondary}
            background={colors.background.tableHeader}
            variante="subtle"
          />
        );
    }
  };

  const auslassung = (indizes: number[], vorAktuell: boolean) => (
    <Tooltip
      title={indizes.map((index) => schritte[index].visual.label).join(" · ")}
      arrow
    >
      <span>
        {vorAktuell ? (
          <StatusChip
            label="…"
            color={colors.brand.primary}
            background={colors.brand.primary}
            variante="outlined"
          />
        ) : (
          <StatusChip
            label="…"
            color={colors.text.secondary}
            background={colors.background.tableHeader}
            variante="subtle"
          />
        )}
      </span>
    </Tooltip>
  );

  const eintraege = eintraegeFuer(schritte.length, sichtbar);
  return (
    <Box ref={containerRef} sx={{ position: "relative", minWidth: 0 }}>
      {/* Hidden copy for measuring – same chips, no layout influence. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          height: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {schritte.map((schritt, index) => (
          <Box
            key={schritt.key}
            ref={(el: HTMLDivElement | null) => {
              messRefs.current[index] = el;
            }}
            sx={{ display: "inline-block" }}
          >
            {chip(schritt, index)}
          </Box>
        ))}
        <Box ref={auslassungRef} sx={{ display: "inline-block" }}>
          {auslassung([], false)}
        </Box>
      </Box>

      <Box
        component="ol"
        aria-label={ariaLabel}
        sx={{
          listStyle: "none",
          m: 0,
          p: 0,
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
        }}
      >
        {eintraege.map((eintrag, position) => {
          const erster =
            eintrag.art === "schritt" ? eintrag.index : eintrag.indizes[0];
          // A line is brand-colored up to the current step.
          const erreicht = erster <= aktuell;
          return (
            <React.Fragment
              key={
                eintrag.art === "schritt"
                  ? schritte[eintrag.index].key
                  : `auslassung-${erster}`
              }
            >
              {position > 0 && (
                <Box
                  component="li"
                  aria-hidden
                  sx={{
                    flex: 1,
                    minWidth: LINE_MIN,
                    mx: `${LINE_MARGIN}px`,
                    borderTop: `${erreicht ? 2 : 1}px solid ${
                      erreicht ? colors.brand.primary : colors.border.light
                    }`,
                  }}
                />
              )}
              <Box
                component="li"
                aria-current={
                  eintrag.art === "schritt" && eintrag.index === aktuell
                    ? "step"
                    : undefined
                }
                sx={{ flexShrink: 0 }}
              >
                {eintrag.art === "schritt"
                  ? chip(schritte[eintrag.index], eintrag.index)
                  : auslassung(eintrag.indizes, erster < aktuell)}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default StatusStrahl;
